import type { PulseFinding } from '../../../api/work';
import './insights.css';

/**
 * The checks a reader could actually go and do.
 *
 * The predecessor's filter is kept verbatim and it is the point of the
 * component: **a suggested check, and not severity `low`**, capped at five.
 * A list of twenty suggestions is a list nobody works through, and a `low`
 * finding's check is by construction not worth interrupting anyone for.
 *
 * Clicking one opens the side panel rather than marking it done — nothing
 * here writes. `work.pulse` produces suggestions; whether they were acted
 * on is not something the panel can know.
 */

const MAX_CHECKS = 5;

interface Props {
  findings: PulseFinding[];
  onSelect?: (finding: PulseFinding) => void;
}

export function SuggestedChecks({ findings, onSelect }: Props) {
  const checks = findings
    .filter((f) => f.suggested_check && f.severity !== 'low')
    .slice(0, MAX_CHECKS);
  if (checks.length === 0) return null;

  const hidden = findings.filter(
    (f) => f.suggested_check && f.severity !== 'low',
  ).length - checks.length;

  return (
    <section className="sc">
      <span className="nb__label">Suggested checks</span>
      <ul className="sc__list">
        {checks.map((f) => (
          <li key={`${f.run_id}-${f.kind}-${f.headline}`}>
            <button
              type="button"
              className="sc__item"
              onClick={() => onSelect?.(f)}
              title={f.headline}
            >
              <span className={`sc__sev sc__sev--${f.severity}`} aria-hidden>
                →
              </span>
              <span className="sc__text">{f.suggested_check}</span>
              <span className="sc__lane in-dim">{f.lane_primary}</span>
            </button>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <span className="in-dim">
          {hidden} more not shown — five is what gets worked through.
        </span>
      )}
    </section>
  );
}
